<template>
  <div class="h-full p-2 w-full mx-auto">
    <div class="w-full mx-auto flex flex-col max-w-7xl">
      <!-- Header section -->
      <div class="flex items-center mt-2 px-2">
        <UsersRound class="w-5 h-5 mr-1" />
        <h1 class="text-xl">
          Manage {{ formatExpeditionName(expeditionsStore.expeditionData?.name) }} Members
        </h1>
      </div>

      <!-- Selection section -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4 px-2 mt-4">
        <div class="bg-white p-4 px-6 rounded-xl border-[1px] border-gray-200">
          <div class="flex items-center gap-2 mb-2">
            <GraduationCap class="w-5 h-5" />
            <h2 class="text-lg font-medium">Batch participants ({{ selectedUsers.length }})</h2>
          </div>

          <UserSelection v-model="selectedUsers" :disabled="!isAdmin" />
        </div>
        <div class="bg-white p-4 px-6 rounded-xl border-[1px] border-gray-200">
          <div class="flex items-center gap-2 mb-2">
            <UserRoundCog class="w-5 h-5" />
            <h2 class="text-lg font-medium">Batch guides</h2>
          </div>
          <GuideSelection v-model="selectedGuides" :disabled="!isAdmin" />
        </div>

        <div class="bg-white p-4 px-6 rounded-xl border-[1px] border-gray-200">
          <div class="flex items-center gap-2 mb-3">
            <UserRoundCog class="w-5 h-5" />
            <h3 class="text-lg font-medium">Main Guide</h3>
          </div>
          <MainGuideSelection
            v-model="selectedMainGuideId"
            :guides="selectedGuides"
            :disabled="!isAdmin"
          />
        </div>
      </div>

      <button
        class="w-fit ml-auto bg-cosmicPurple text-xs text-white p-2 px-4 rounded hover:bg-cosmicPurple/80 hover:-translate-y-[1px] transition-all duration-200 mx-4 mt-4"
        @click="submitExpeditionMembers"
        :disabled="!isAdmin || membersStore.manageExpeditionMembersLoading"
      >
        <Loader2
          v-if="membersStore.manageExpeditionMembersLoading"
          class="w-4 h-4 mr-2 animate-spin"
        />
        Submit
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useMembersStore } from '@/stores/members'
import { useExpeditionsStore } from '@/stores/expeditions'
import UserSelection from '@/components/manage/UserSelection.vue'
import GuideSelection from '@/components/manage/GuideSelection.vue'
import MainGuideSelection from '@/components/manage/MainGuideSelection.vue'
import type { User } from '@/types/user'
import { UsersRound, Loader2, UserRoundCog, GraduationCap } from 'lucide-vue-next'
import { useToast } from 'primevue/usetoast'
import { formatExpeditionName } from '@/utils/formatExpeditionName'
import { useAuth } from '@/composables/useAuth'

const route = useRoute()
const membersStore = useMembersStore()
const expeditionsStore = useExpeditionsStore()
const { isAdmin } = useAuth()

const toast = useToast()

const expeditionId = computed(() => route.params.id as string)
const selectedUsers = ref<User[]>([])
const selectedGuides = ref<User[]>([])
const selectedMainGuideId = ref<string | null>(null)

const loadMembersData = async () => {
  await Promise.all([
    membersStore.fetchExpeditionAdventurers(expeditionId.value),
    membersStore.fetchExpeditionGuides(expeditionId.value),
    expeditionsStore.fetchExpeditionData(expeditionId.value)
  ])
  selectedUsers.value = membersStore.adventurers
  selectedGuides.value = membersStore.guides

  if (expeditionsStore.expeditionData?.main_guide) {
    selectedMainGuideId.value = String(expeditionsStore.expeditionData.main_guide.id)
  }

  if (expeditionsStore.expeditionData) {
    document.title = `${formatExpeditionName(expeditionsStore.expeditionData.name)} Members | Outtalent Campus`
  }
}

watch(expeditionId, () => {
  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  loadMembersData()
}, { immediate: true })

async function submitExpeditionMembers() {
  console.log('submitExpeditionMembers called')
  console.log('Inspecting membersStore:', membersStore)
  try {
    await membersStore.manageExpeditionMembers(
      expeditionId.value,
      selectedUsers.value,
      selectedGuides.value,
      selectedMainGuideId.value ? Number(selectedMainGuideId.value) : null
    )
    console.log('manageExpeditionMembers finished successfully (from component)')
    toast.add({
      severity: 'success',
      summary: 'Success',
      detail: 'Expedition members updated successfully',
      life: 3000
    })
  } catch (error) {
    console.error('Error during submitExpeditionMembers:', error)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to update expedition members',
      life: 3000
    })
  }
}
</script>
